import React, {Fragment, useCallback, useEffect, useState} from "react";
import {
    Button,
    Checkbox,
    Form,
    Input,
    message as antdMessage,
    notification,
    PageHeader,
    Popconfirm,
    Select,
    Space,
    Upload
} from "antd";
import {InfoCircleOutlined, PaperClipOutlined, SaveOutlined, SendOutlined} from "@ant-design/icons";
import {sendEmailForDirectMessage, uploadAttachment, visualizeLoad} from "../api/api";
import {useHistory, useLocation} from "react-router-dom";
import {MAIL_STATUS} from "../utils/constants";
import "../styles/Em.css";

const EMerchants = ({emList, value, onChange}) => {
    const [emId, setEMId] = useState(value)

    return <Space>
        <Select
            placeholder={'Select E Merchant/s'}
            mode={"multiple"}
            disabled={emId===null}
            optionFilterProp={"children"}
            options={emList}
            value={emId}
            onChange={fieldValue => {
                onChange(fieldValue)
                setEMId(fieldValue)
            }}
            filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
        />
        <Checkbox
            checked={emId===null}
            onChange={e => {
                onChange(e.target.checked?null:undefined)
                setEMId(e.target.checked?null:undefined)
            }}
        >All E Merchants</Checkbox>
    </Space>
}

const FileUpload = ({value, onChange}) => {
    return <Upload
        maxCount={1}
        defaultFileList={value?.filename?[{uid: '1', name: value?.filename, status: "done"}]:undefined}
        beforeUpload={(file => {
            if(file.size>10000000) {
                antdMessage.error('Maximum file size is 10MB')
                return false
            }
            return true
        })}
        customRequest={async opt => {
            const formData = new FormData()
            formData.append(opt.file.name, opt.file)
            const {data} = await uploadAttachment(formData)
            onChange({oid: data[0], filename: opt.file.name})
            opt.onSuccess(data, opt.file)
        }}
    >
        <Button className={'margin-five'} icon={<PaperClipOutlined />}>Attach File</Button>
    </Upload>
}

const AddMessage = () => {
    const location = useLocation()
    const history = useHistory()
    let {initialFieldData} = location.state
    if(initialFieldData?.oid && initialFieldData?.filename) {
        initialFieldData = {
            ...initialFieldData,
            upload: {
                oid: initialFieldData?.oid,
                filename: initialFieldData?.filename
            }
        }
    }

    const [form] = Form.useForm()
    const [emList, setEMList] = useState()

    const loadEMs = useCallback(async () => {
        const payload = {
            tableName: 'enums',
            filter: [{name: 'type', value: 'em_name'}]
        }
        const {data: {rows = []} = {}} = await visualizeLoad(payload)
        setEMList(rows[0]?.values?.map(([id, name]) => ({label: name, value: parseInt(id)})))
    }, [])

    useEffect(() => {
        loadEMs()
    }, [])

    const handleSubmit = useCallback(async status => {
        form.submit()
        const {em_id, subject, message, upload} = form.getFieldsValue(['em_id', 'subject', 'message', 'upload'])
        if((em_id || em_id===null) && subject) {
            const payload = {
                em_id,
                subject,
                message,
                ...upload,
                status,
                sent_date: status===MAIL_STATUS.SENDING?new Date():undefined
            }
            const {data, status: statusCode} = await sendEmailForDirectMessage(payload)
            if(statusCode===200) {
                notification.success({message: `Message ${status===MAIL_STATUS.DRAFT?'saved':'sent'} successfully`})
                history.push({
                    pathname: '/view/direct-message'
                })
            }
        }
    }, [form])

    const { TextArea } = Input
    const { Item } = Form
    return <Fragment>
        <PageHeader title={'New Message'}/>
        <div className={'direct-message-new'}>
            <Form
                name={'direct_message'}
                form={form}
                initialValues={{ message: 'Dear Customer, \n\t', ...initialFieldData }}
            >
                <Item
                    label={'To'}
                    name={'em_id'}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value===null || (typeof getFieldValue('em_id')==='object' && getFieldValue('em_id').length>0)) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Please select E-Merchant/s'));
                            },
                        })
                    ]}
                >
                    <EMerchants
                        emList={emList}
                        value={form.getFieldValue('em_id')}
                        onChange={value => form.setFieldsValue({em_id: value})}
                    />
                </Item>
                <Item name={'subject'} rules={[{ required: true, message: 'Please enter subject' }]}>
                    <Input placeholder="Subject" />
                </Item>
                <Item name={'message'}>
                    <TextArea rows={15} />
                </Item>
                <Item name={'upload'}>
                    <FileUpload
                        value={form.getFieldValue('upload')}
                        onChange={value => form.setFieldsValue({upload: value})}
                    />
                </Item>
                <Item>
                    <Space className={'direct-message-btn'}>
                        <Popconfirm
                            title={'All changed will be lost, please confirm closing window'}
                            okText={'Close'}
                            cancelText={'Cancel'}
                            onConfirm={() => {
                                history.push({
                                    pathname: '/view/direct-message'
                                })
                            }}
                        >
                            <Button>Cancel</Button>
                        </Popconfirm>
                        <Button
                            type={'primary'}
                            icon={<SaveOutlined />}
                            onClick={() => handleSubmit(MAIL_STATUS.DRAFT)}
                        >Save</Button>
                        <Popconfirm
                            title={'Please confirm sending of the message'}
                            okText={'Confirm'}
                            cancelText={'Cancel'}
                            onConfirm={() => handleSubmit(MAIL_STATUS.SENDING)}
                            icon={<InfoCircleOutlined className={'color-blue'} />}
                        >
                            <Button type={'primary'} icon={<SendOutlined />}>Send</Button>
                        </Popconfirm>

                    </Space>
                </Item>
            </Form>
        </div>
    </Fragment>
}

export default AddMessage